/**
 * @module app.ext.mcg-informed
 */

import { autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/ext/mcg-informed/assessment-context';
import { IQuestionComponent, IQuestionConfig } from 'app/ext/mcg-informed/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { QuestionBase } from 'app/ext/mcg-informed/question-base';
import { McgInformedDataMethods as mcgMethods } from 'app/ext/mcg-informed/data-methods';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import { fireEvent } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';


@autoinject
export class QuestionCheckboxCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////

    protected selectedChoices: Mcg.Choice[] = [];
    protected selectedGuidelineLinks: Mcg.GuidelineLink[] = [];
    protected existingAttachments: string[] = [];
    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) ******************////

    constructor(
        private readonly element: Element,
        protected readonly context: AssessmentContext,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) throw new Error("No Assessment context is available");

        if(!this.context.questionComponents.includes(this)) this.context.questionComponents.push(this);
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.readonly = config.readonly;
        this.existingAttachments = config.existingAttachments;
        this.labelId = config.labelId;
    }

    protected attached(): void {
        this.loadPreviousAnswer();
    }


    ////****************** Events ***********************************************////

    protected choiceChanged(choice: Mcg.Choice | null, checked: boolean) {
        this.context.unsavedChanges = true;

        if(!choice) return;

        if(choice.mutuallyExclusive || this.selectedChoices.some(sc => sc.mutuallyExclusive)) {
            const choiceQuestionUid: string = choice.uid.substring(0, choice.uid.lastIndexOf('-'));
            const questionComponent: IQuestionComponent | undefined = this.context.questionComponents.find(qc => qc.question?.uid === choiceQuestionUid);

            if(questionComponent) {
                if(choice.mutuallyExclusive && checked) {
                    questionComponent.question?.answersSet.forEach(a => {
                        if(a.uid !== choice.uid && this.selectedChoices.some(sc => sc === a)) this.selectedChoices.splice(this.selectedChoices.indexOf(a), 1);
                    });
                }

                if(this.selectedChoices.some(sc => sc.mutuallyExclusive) && choice.uid !== this.selectedChoices.find(sc => sc.mutuallyExclusive)?.uid) {
                    questionComponent.question?.answersSet.forEach(a => {
                        if(a.uid !== choice.uid && this.selectedChoices.some(sc => sc === a)) this.selectedChoices.splice(this.selectedChoices.indexOf(a), 1);
                    });
                }
            }
        }

        this.isValid = this.selectedChoices.length > 0;

        this.context.questionComponents.forEach(qc => {
            if(!qc.question) return;
            qc.question.answersSet.forEach(a => {
                if(a.uid === choice?.uid) mcgMethods.hideFollowupsRecursive(qc.question, this.context);
            });
        });

        if(choice && choice.questionRefSet?.length > 0) {
            choice.questionRefSet.forEach(qr => {
                if(checked) this.context.selectedQuestionReferenceIds += `|${qr.id}`;
                else this.context.selectedQuestionReferenceIds = this.context.selectedQuestionReferenceIds.replace(`|${qr.id}`, '');
            });
        }
    }

    protected guidelineLinkChoiceChanged(): void {
        this.context.unsavedChanges = true;
    }


    ////****************** Functions ****************************************************////

    private async loadPreviousAnswer(): Promise<void> {
        if(!this.question || !this.context.attachment.currentAnswers) return;

        try {
            this.selectedChoices.push(...this.question.answersSet.filter(a => a.selected));

            this.selectedChoices.forEach(choice => {
                if(choice.questionRefSet?.length > 0) {
                    choice.questionRefSet.forEach(qr => {
                        this.context.selectedQuestionReferenceIds += `|${qr.id}`;
                    });
                }
            });



        }
        catch(error) {
            const questionId = this.question && this.question.id;
            const description = this.question && this.question.questionText || '';

            this.logger.error(`Unable to find existing answer(s) for question ${questionId} (${description})`);
            throw error;
        }
    }

    ////****************** IQuestionComponent ****************************************************////

    public saveAnswer = null;  // Not Implemented

    public getAnswers(): Mcg.Choice[] {
        return this.selectedChoices;
    }

    public getGuidelineLinks(): Mcg.GuidelineLink[] {
        return this.selectedGuidelineLinks;
    }

    public clearSelectedAnswers(): void {
        this.selectedChoices = [];
    }

}
